<script setup>
import moment from "moment-timezone"
import InquiryBookedSheet from '@core/components/inquiry/InquiryBookedSheet.vue'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { DRAFT_LABEL, requestedNames, returnedDraftNote, reviewLine } from '@/utils/requestApproval'
import { mediaUrl } from '@core/utils/mediaUrl'
import {newBookingTimingText, numberWithCommas} from "@/plugins/misc"
import {useUsersStore, useReservationsStore, useRestaurantsStore, usePreferredStatusStore, useSupportStore} from "@/stores"
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import FriendsLikedChips from '@core/components/FriendsLikedChips.vue'
import CmsText from '@/@core/components/CmsText.vue'

const props = defineProps({
  item: Object,
  experienced: Boolean
})

const emit = defineEmits(['newDraft', 'showRestaurantDetails'])
// RM-400 — "Booked?" on a group/private dining inquiry (InquiryBookedSheet)
const isBookedSheetOpen = ref(false)
const router = useRouter()
const supportStore = useSupportStore()
const bookingHeartRef = ref(null)
const openFansDrawer = () => bookingHeartRef.value?.openFavoritedBy()
const isConfirmDeleteDialogOpen = ref(false)
const isConfirmRescindDialogOpen = ref(false)
const isLateCancelDialogOpen = ref(false)
// RM-164: within-24h request-to-cancel form state
const cancellationReason = ref('')
const requestingCancellation = ref(false)
const cancellationRequested = ref(false)
// Once-only: a request can be sent exactly once — tapping Request to Cancel
// again shows the "already sent" state instead of the form.
const cancellationAlreadyRequested = ref(false)
const cancellationRequestError = ref('')
const reservationIdSelected = ref(null)
const isBillDetailsDrawerOpen = ref(false)
const isUploadingReceipt = ref(false)
const removingReceiptImageId = ref(null)
const receiptUploadError = ref('')
const receiptFileInputRef = ref(null)
const failedReceiptThumbs = ref({})

// Initialize stores
const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()
const restaurantsStore = useRestaurantsStore()
const preferredStatusStore = usePreferredStatusStore()

const {
  myUser,
  users,
  isRunningInStandAlone,
  isShowingPreferredRequestInfoDialog
} = storeToRefs(usersStore)

const {
  cancelingReservation
} = storeToRefs(reservationsStore)

const {
  restaurants,
  favoriteNotes
} = storeToRefs(restaurantsStore)

// Get restaurant status for the booking
const restaurantStatus = computed(() => {
  const restaurantName = props.item.confirmedRestaurant || (props.item.requestedRestaurantsArray && props.item.requestedRestaurantsArray.length > 0 ? props.item.requestedRestaurantsArray[0] : null)
  if (restaurantName && restaurants.value) {
    const restaurant = restaurants.value.find(r => r.name === restaurantName && r.market === props.item.market)
    
    return restaurant?.status || null
  }
  
  return null
})

const formattedSpend = booking => {
  if (booking.actualSpendTotal !== null && booking.actualSpendTotal !== undefined && booking.actualSpendTotal !== '') {
    return `$${numberWithCommas(booking.actualSpendTotal)}`
  }

  // Check if we have the necessary data to calculate spend
  if (!booking.minSpendPerPerson || !booking.partySize) {
    return 'Not selected yet'
  }

  // Match ViewReservationDialog total: base + 20% gratuity + 12% tax
  const baseSpend = booking.minSpendPerPerson * booking.partySize
  const calculatedSpend = Math.round(baseSpend + (baseSpend * 0.2) + (baseSpend * 0.12))

  // Double-check the result isn't NaN
  if (isNaN(calculatedSpend)) {
    return 'Not selected yet'
  }

  return `$${numberWithCommas(calculatedSpend)}`
}

// RM-400 — how a group/private dining inquiry's status reads to the member
// (lib/inquiryStatus.js memberLabelFor): "Approve?" is RM's word, never shown.
const isInquiry = item => item?.reservationType === 'private'
// An inquiry to several restaurants names them all; one restaurant's group under that list reads as
// if it were the whole inquiry's (Kapil, 10/21), so the line stays blank for a multi-restaurant inquiry.
const hideGroupLine = computed(() => isInquiry(props.item) && (props.item?.requestedRestaurantsArray || []).length > 1)
const inquiryLabel = item => {
  if (!isInquiry(item)) return null
  if (item.status === 'Approve?') return 'Booked, pending RM approval'
  if (item.status === 'Accepted') return 'Booked'
  
  return null
}
const inquiryOtherRestaurants = item => (item?.requestedRestaurantsArray || []).filter(name => name !== item?.confirmedRestaurant)
const translateStatus = {
  // RM-359: being in Bookings does not mean it was submitted.
  Draft: DRAFT_LABEL,
  Submitted: 'Submitted',
  Accepted: 'Accepted',
  Processing: 'Processing',
  "Awaiting Response from Restaurant": 'Awaiting Response',
  "In Review": 'In Review',
  "Member Action": 'Member Action',
  // RM-359: submitted, RM reviews it before it goes to the restaurant. (A
  // request RM doesn't send goes back to Draft — there is no status for it.)
  "Pending RM Review": 'Pending RM Review',
  "Not Available": 'Not Available',
  "No Response": 'No Response',
  "Declined": 'Declined',
  "Rescinded": 'Canceled',
  "Canceled": 'Canceled',
  "Rescinding": "Canceled",
  "Canceling" : "Canceled"
}

// Defensive function to handle reservation store operations
const safeReservationOperation = (operation, ...args) => {
  if (reservationsStore && typeof reservationsStore[operation] === 'function') {
    return reservationsStore[operation](...args)
  } else {
    console.warn(`Reservation store operation '${operation}' not available`)
  }
}

const showRestaurantDetails = (market, restaurant) => {
  emit('showRestaurantDetails', market, restaurant)
}

// Open the member↔RM support thread in Messages (same pattern as
// Notifications.vue — "/support" is an AI-chat-only link convention and is
// not a real route).
const messageSupport = async () => {
  isLateCancelDialogOpen.value = false
  try {
    const conv = await supportStore.getOrCreateSupportConversation()
    if (conv?._id) {
      await router.push({ path: '/messages', query: { conv: conv._id } })
    }
  } catch (err) {
    console.error('Failed to open support thread', err)
  }
}

// RM-164: open/reset the within-24h request-to-cancel dialog
const openLateCancelDialog = () => {
  // Once-only: a pending request must not be re-sent — show the
  // already-sent state instead of the form.
  cancellationAlreadyRequested.value = !!props.item?.cancellationRequestedAt
  cancellationReason.value = ''
  cancellationRequested.value = false
  cancellationRequestError.value = ''
  requestingCancellation.value = false
  isLateCancelDialogOpen.value = true
}

// RM-164: submit the within-24h request. Does not change reservation status —
// it records the reason + fires the restaurant decision email; RM resolves manually.
const submitCancellationRequest = async () => {
  if (!cancellationReason.value.trim()) return
  requestingCancellation.value = true
  cancellationRequestError.value = ''
  try {
    await reservationsStore.requestCancellation(props.item._id, cancellationReason.value.trim())
    cancellationRequested.value = true
  } catch (error) {
    // 409 = a request is already pending (server once-only guard) — show the
    // already-sent state rather than an error.
    if (error?.response?.status === 409) {
      cancellationAlreadyRequested.value = true
    } else {
      console.error('Error requesting cancellation:', error)
      cancellationRequestError.value = 'Could not submit your request. Please try again.'
    }
  } finally {
    requestingCancellation.value = false
  }
}

const openBillDetailsDrawer = () => {
  isBillDetailsDrawerOpen.value = true
}

const reservationId = computed(() => props.item?.id || props.item?._id)
const receiptImageIds = computed(() => (
  Array.isArray(props.item?.receiptImageIds) ? props.item.receiptImageIds : []
))

const formattedFnbSpend = computed(() => {
  if (props.item?.minSpendPerPerson && props.item?.partySize) {
    return `$${numberWithCommas(props.item.minSpendPerPerson * props.item.partySize)}`
  }

  return 'Not selected yet'
})

const formattedTotalSpend = computed(() => {
  const spend = formattedSpend(props.item)

  return spend && spend !== '$' ? spend : 'Not selected yet'
})

const triggerReceiptFilePicker = () => {
  receiptFileInputRef.value?.click()
}

const uploadReceiptImage = async event => {
  const file = event?.target?.files?.[0]
  if (!file || !reservationId.value) return

  receiptUploadError.value = ''
  isUploadingReceipt.value = true

  try {
    const formData = new FormData()
    formData.append('image', file)

    const mediaUploadResponse = await fetch('/media/', {
      method: 'POST',
      body: formData,
      credentials: 'include'
    })

    if (!mediaUploadResponse.ok) {
      throw new Error('Unable to upload receipt image.')
    }

    const imageId = (await mediaUploadResponse.text()).trim()
    if (!imageId) {
      throw new Error('Uploaded receipt image did not return an imageId.')
    }

    await reservationsStore.addReceiptImageToReservation(reservationId.value, imageId)
  } catch (error) {
    console.error('Error uploading receipt image:', error)
    receiptUploadError.value = 'Could not upload receipt. Please try again.'
  } finally {
    isUploadingReceipt.value = false
    if (event?.target) event.target.value = ''
  }
}

const viewReceiptImage = imageId => {
  if (!imageId) return
  window.open(mediaUrl(imageId, 'original'), '_blank', 'noopener,noreferrer')
}

const hasReceiptThumbnail = imageId => !failedReceiptThumbs.value[imageId]

const markReceiptThumbFailed = imageId => {
  failedReceiptThumbs.value[imageId] = true
}

const clearReceiptThumbFailed = imageId => {
  delete failedReceiptThumbs.value[imageId]
}

const removeReceiptImage = async imageId => {
  if (!imageId || !reservationId.value) return

  receiptUploadError.value = ''
  removingReceiptImageId.value = imageId
  try {
    await reservationsStore.removeReceiptImageFromReservation(reservationId.value, imageId)
  } catch (error) {
    console.error('Error removing receipt image:', error)
    receiptUploadError.value = 'Could not remove receipt. Please try again.'
  } finally {
    clearReceiptThumbFailed(imageId)
    removingReceiptImageId.value = null
  }
}

// Check if reservation is accepted and within 24 hours (but not in the past)
const isWithin24Hours = computed(() => {
  if (props.item.status !== 'Accepted') return false
  const reservationTime = moment.tz(
    props.item.date + ' ' + (props.item.confirmedTime || props.item.preferredTime),
    'YYYY-MM-DD hh:mm:a',
    props.item.timeZone?.identifier || 'America/New_York'
  )

  // Don't show for past reservations
  if (moment().isAfter(reservationTime)) return false

  const cutoffTime = reservationTime.clone().subtract(24, 'hours')
  
  return moment().isSameOrAfter(cutoffTime)
})

// RM-359: why RM returned this request to Draft (null for an ordinary draft).
const returnedNote = computed(() => returnedDraftNote(props.item))

// Check if reservation is cancelled
const isCancelled = computed(() => {
  return ['Canceled', 'Cancelled', 'Rescinded', 'Canceling', 'Rescinding'].includes(props.item.status)
})

// Get the primary restaurant name and object for this booking
const primaryRestaurantName = computed(() => {
  return props.item.confirmedRestaurant || (props.item.requestedRestaurantsArray?.length > 0 ? props.item.requestedRestaurantsArray[0] : null)
})

const primaryRestaurant = computed(() => {
  const name = primaryRestaurantName.value
  if (name && restaurants.value) {
    return restaurants.value.find(r => r.name === name && r.market === props.item.market) || null
  }
  
  return null
})

const primaryRestaurantId = computed(() => primaryRestaurant.value?._id || primaryRestaurant.value?.id)

const isMemberPreferred = computed(() =>
  myUser.value && myUser.value.userLevel !== 'dummy' && preferredStatusStore.isPreferredAt(primaryRestaurantId.value)
)

const memberPreferredGroupName = computed(() =>
  preferredStatusStore.groupNameForRestaurant(primaryRestaurantId.value)
)

// Favorite state
const favorited = computed(() => {
  if (myUser.value?._id && primaryRestaurant.value) {
    const favoritedBy = primaryRestaurant.value.favoritedBy
    if (favoritedBy && Array.isArray(favoritedBy)) {
      if (favoritedBy.some(id => id?.toString() === myUser.value._id?.toString())) return true
    }
    if (myUser.value.favoritedRestaurants?.includes(primaryRestaurantId.value)) return true
  }
  
  return false
})

// Note dialog
const isNoteDialogOpen = ref(false)
const noteText = ref('')
const savingNote = ref(false)

const existingNote = computed(() => {
  const id = primaryRestaurantId.value
  
  return id ? favoriteNotes.value[id] : null
})

const hasNote = computed(() => !!existingNote.value?.note)

const openNoteDialog = () => {
  noteText.value = existingNote.value?.note || ''
  isNoteDialogOpen.value = true
}

const noteError = ref('')

const saveNote = async () => {
  savingNote.value = true
  noteError.value = ''

  const res = await restaurantsStore.saveFavoriteNote(primaryRestaurantId.value, noteText.value)

  savingNote.value = false

  // A save that did not happen must say so. Closing silently read as "saved".
  if (res && res.ok === false) {
    noteError.value = res.message

    return
  }
  isNoteDialogOpen.value = false
}

// Feature flag: restaurant messaging is not yet available
const RESTAURANT_MESSAGING_ENABLED = false

// Show "Message Restaurant" when status is not Draft and restaurant is Preferred
const canMessageRestaurant = computed(() => {
  if (!RESTAURANT_MESSAGING_ENABLED) return false
  // RM-359: nor a request the restaurant doesn't have (waiting for RM)
  if (!props.item || ['Draft', 'Pending RM Review'].includes(props.item.status)) return false
  if (restaurantStatus.value !== 'Preferred') return false

  return !!primaryRestaurant.value?._id
})

function openMessageRestaurant() {
  const restaurantId = primaryRestaurant.value?._id
  const reservationId = props.item._id
  router.push({
    path: '/messages',
    query: {
      restaurantId,
      reservationId,
    },
  })
}
</script>

<template>
  <div
    :key="item._id"
    class="booking-container cursor-pointer"
  >
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDeleteDialogOpen"
      confirmation-msg="Are you sure you want to delete this?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="safeReservationOperation('deleteReservation', item._id)"
    />
    <InquiryBookedSheet
      v-if="isInquiry(item)"
      v-model="isBookedSheetOpen"
      :reservation="item"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmRescindDialogOpen"
      confirmation-msg="Are you sure you want to Cancel this?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="safeReservationOperation('setReservationStatus', item._id, 'Rescinded')"
    />

    <!-- RM-164: Within-24h Request-to-Cancel Dialog (in-app, no status change) -->
    <VDialog
      v-model="isLateCancelDialogOpen"
      max-width="440"
    >
      <VCard>
        <!-- Once-only: a request is already pending — no form, no resend -->
        <template v-if="cancellationAlreadyRequested">
          <VCardText class="pa-6 text-center">
            <VIcon
              color="primary"
              size="40"
              class="mb-3"
            >
              mdi-email-check-outline
            </VIcon>
            <div class="text-h6 mb-2">
              Request for cancellation already sent
            </div>
            <div class="text-body-2 text-medium-emphasis">
              If you'd like to further discuss, email
              <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
              or <a
                href="javascript:;"
                @click.prevent="messageSupport"
              >message Support</a>.
            </div>
          </VCardText>
          <VCardActions class="pa-4 pt-0">
            <VSpacer />
            <VBtn
              color="primary"
              variant="elevated"
              @click="isLateCancelDialogOpen = false"
            >
              Done
            </VBtn>
          </VCardActions>
        </template>

        <!-- Confirmation state after a successful submit -->
        <template v-else-if="cancellationRequested">
          <VCardText class="pa-6 text-center">
            <VIcon
              color="primary"
              size="40"
              class="mb-3"
            >
              mdi-check-circle-outline
            </VIcon>
            <div class="text-h6 mb-2">
              Request sent
            </div>
            <div class="text-body-2 text-medium-emphasis">
              The RM team is reviewing your cancellation request with {{ primaryRestaurantName || 'the restaurant' }}.
              You’ll hear back soon. Your reservation stays active until it’s resolved.
            </div>
          </VCardText>
          <VCardActions class="pa-4 pt-0">
            <VSpacer />
            <VBtn
              color="primary"
              variant="elevated"
              @click="isLateCancelDialogOpen = false"
            >
              Done
            </VBtn>
          </VCardActions>
        </template>

        <!-- Request form -->
        <template v-else>
          <VCardTitle class="text-h6 pa-4">
            Request to Cancel
          </VCardTitle>
          <VCardText class="pa-4 pt-0">
            <!--
              Within-24h flow shows only the within-24h notice (the general
              "think twice" warning is shown on the >24h cancel dialog). 
            -->
            <CmsText
              k="reservation.cancellation.within24hNotice"
              tag="p"
              class="text-body-2 text-medium-emphasis mb-4"
            />
            <VTextarea
              v-model="cancellationReason"
              label="Reason for cancelling"
              placeholder="Let the restaurant know why you need to cancel"
              rows="3"
              auto-grow
              counter
              :rules="[v => !!(v && v.trim()) || 'A reason is required']"
              style="font-size: 16px;"
            />
            <div
              v-if="cancellationRequestError"
              class="text-error text-body-2 mt-1"
            >
              {{ cancellationRequestError }}
            </div>
          </VCardText>
          <VCardActions class="pa-4 pt-0">
            <VBtn
              variant="tonal"
              color="secondary"
              @click="isLateCancelDialogOpen = false"
            >
              Never mind
            </VBtn>
            <VSpacer />
            <VBtn
              color="primary"
              variant="elevated"
              :loading="requestingCancellation"
              :disabled="!cancellationReason.trim() || requestingCancellation"
              @click="submitCancellationRequest"
            >
              Request Cancellation
            </VBtn>
          </VCardActions>
        </template>
      </VCard>
    </VDialog>
    <VBottomSheet v-model="isBillDetailsDrawerOpen">
      <VCard class="bill-details-sheet">
        <div class="sheet-handle" />
        <div class="bill-sheet-header">
          <div>
            <div class="bill-sheet-title">
              {{ item.confirmedRestaurant || item.requestedRestaurantsArray?.[0] || 'N/A' }}
            </div>
            <!--
              RM-150: member profile details shown when the actor is not the
              owner (admin account viewing a reservation booked on behalf). 
            -->
            <div
              v-if="item?.owner?.fullName && item?.owner?.email && myUser?.email && (item.owner.email.toLowerCase() !== myUser.email.toLowerCase())"
              class="bill-sheet-member-line"
            >
              Booking for <strong>{{ item.owner.fullName }}</strong>
              <span
                v-if="item.owner.email"
                class="bill-sheet-member-email"
              >&middot; {{ item.owner.email }}</span>
            </div>
            <div
              v-if="item?.submittedBy?.firstName && item?.submittedBy?.email && item.owner?.email && (item.submittedBy.email.toLowerCase() !== item.owner.email.toLowerCase())"
              class="bill-sheet-member-line"
            >
              Submitted by {{ item.submittedBy.firstName }} {{ item.submittedBy.lastName }}
            </div>
          </div>
          <VBtn
            icon
            variant="text"
            size="small"
            @click="isBillDetailsDrawerOpen = false"
          >
            <VIcon size="20">
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <div
          v-if="item.date || item.partySize || item.occasion"
          class="bill-sheet-subtitle"
        >
          <span v-if="item.date">{{ moment(item.date, 'YYYY-MM-DD').format('ddd M/D/YY') }}</span>
          <span v-if="item.date && (item.preferredTime || item.partySize || item.occasion)">•</span>
          <span v-if="item.preferredTime">{{ moment(item.date + ' ' + item.preferredTime, 'YYYY-MM-DD hh:mm:a').format('h:mma') }}</span>
          <span v-if="item.preferredTime && (item.partySize || item.occasion)">•</span>
          <span v-if="item.partySize">Party of {{ item.partySize }}</span>
          <span v-if="item.partySize && item.occasion">•</span>
          <span v-if="item.occasion">{{ item.occasion }}</span>
        </div>
        <div
          v-if="experienced && item.reservationType == 'general' && item.status == 'Accepted' && !item.directBooking"
          class="bill-sheet-timing"
        >
          Secured {{ newBookingTimingText(item) }}
        </div>
        <div
          v-if="item.additionalNotes"
          class="bill-sheet-note"
        >
          Notes: {{ item.additionalNotes }}
        </div>

        <!-- Active reservation details: submitted, expiration, time range -->
        <div
          v-if="!experienced"
          class="bill-active-details"
        >
          <div
            v-if="item.submittedAt"
            class="bill-detail-row"
          >
            <span class="bill-detail-label">Submitted</span>
            <span class="bill-detail-value">{{ moment(item.submittedAt).tz(item.timeZone?.identifier || 'America/New_York').format('ddd M/D h:mma') }}</span>
          </div>
          <!-- RM-359: while RM reviews it, the deadline the member was given at submit (it doesn't move) -->
          <div
            v-if="(item.requestExpirationTime && !isInquiry(item)) || (item.status === 'Pending RM Review' && item.pendingExpirationTime)"
            class="bill-detail-row"
          >
            <span class="bill-detail-label">Expires</span>
            <span class="bill-detail-value">{{ moment(item.requestExpirationTime || item.pendingExpirationTime).tz(item.timeZone?.identifier || 'America/New_York').format('ddd M/D h:mma') }}</span>
          </div>
          <div
            v-if="item.earliestTime || item.latestTime"
            class="bill-detail-row"
          >
            <span class="bill-detail-label">Preferred Time Range</span>
            <span class="bill-detail-value">
              <template v-if="item.earliestTime && item.latestTime">
                {{ item.earliestTime }} – {{ item.latestTime }}
              </template>
              <template v-else-if="item.earliestTime">
                From {{ item.earliestTime }}
              </template>
              <template v-else>
                Until {{ item.latestTime }}
              </template>
            </span>
          </div>
        </div>

        <div
          v-if="isMemberPreferred"
          class="bill-preferred-banner"
        >
          <VIcon
            icon="mdi-star-check"
            size="18"
            class="mr-1"
          />
          <template v-if="memberPreferredGroupName">
            <span>{{ $t('restaurant.preferred.bannerTitleViaGroup', { groupName: memberPreferredGroupName }) }}</span>
          </template>
          <template v-else>
            <CmsText
              k="restaurant.preferred.bannerTitle"
              tag="span"
            />
          </template>
        </div>
        <div
          v-else
          class="bill-spend-card bill-spend-card-combined"
        >
          <div class="bill-spend-column">
            <div class="bill-spend-label">
              Guaranteed F&B Spend
            </div>
            <div class="bill-spend-value">
              {{ formattedFnbSpend }}
            </div>
          </div>
          <div class="bill-spend-divider" />
          <div class="bill-spend-column bill-spend-column-total">
            <div class="bill-spend-label">
              Total (w/tax & tip)
            </div>
            <div class="bill-spend-value">
              {{ formattedTotalSpend }}
            </div>
          </div>
        </div>

        <div
          v-if="experienced"
          class="bill-receipt-cta"
        >
          <div class="bill-receipt-copy">
            Exceeded spend? Upload receipt to update
          </div>
          <div class="bill-receipt-subcopy">
            Images and PDFs are supported.
          </div>
          <VBtn
            color="primary"
            variant="flat"
            rounded="lg"
            prepend-icon="mdi-paperclip"
            :loading="isUploadingReceipt"
            :disabled="isUploadingReceipt"
            @click="triggerReceiptFilePicker"
          >
            Upload Photo or PDF
          </VBtn>
          <input
            ref="receiptFileInputRef"
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            style="display: none;"
            @change="uploadReceiptImage"
          >
        </div>

        <div
          v-if="experienced && receiptUploadError"
          class="bill-error"
        >
          {{ receiptUploadError }}
        </div>

        <div
          v-if="experienced"
          class="bill-receipts-header"
        >
          <div class="bill-receipts-title">
            Receipts
          </div>
          <VChip
            size="small"
            variant="tonal"
            color="primary"
          >
            {{ receiptImageIds.length }}
          </VChip>
        </div>

        <div
          v-if="experienced && !receiptImageIds.length"
          class="bill-empty-state"
        >
          <VIcon
            icon="mdi-receipt-text-outline"
            size="20"
            color="medium-emphasis"
          />
          No receipts uploaded yet.
        </div>
        <div v-else-if="experienced">
          <div
            v-for="imageId in receiptImageIds"
            :key="imageId"
            class="receipt-image-item"
          >
            <img
              v-if="hasReceiptThumbnail(imageId)"
              :src="mediaUrl(imageId, 'thumb')"
              alt="Receipt image"
              class="receipt-image-preview cursor-pointer"
              @click="viewReceiptImage(imageId)"
              @error="markReceiptThumbFailed(imageId)"
              @load="clearReceiptThumbFailed(imageId)"
            >
            <div
              v-else
              class="receipt-pdf-preview cursor-pointer"
              @click="viewReceiptImage(imageId)"
            >
              <VIcon
                icon="mdi-file-pdf-box"
                size="28"
                color="#cf2f2f"
              />
              <span class="receipt-pdf-preview-text">PDF Receipt</span>
            </div>
            <div class="d-flex align-center mt-2">
              <VBtn
                variant="text"
                size="small"
                color="primary"
                prepend-icon="mdi-open-in-new"
                @click="viewReceiptImage(imageId)"
              >
                View
              </VBtn>
              <VSpacer />
              <VBtn
                variant="text"
                size="small"
                color="error"
                prepend-icon="mdi-trash-can-outline"
                :loading="removingReceiptImageId === imageId"
                @click="removeReceiptImage(imageId)"
              >
                Remove
              </VBtn>
            </div>
          </div>
        </div>
      </VCard>
    </VBottomSheet>

    <button
      class="add-btn-left"
      aria-label="Add to request"
      @click.stop="emit('newDraft', item.market, item.confirmedRestaurant || (item.requestedRestaurantsArray && item.requestedRestaurantsArray.length > 0 ? item.requestedRestaurantsArray[0] : item.preferredRestaurant))"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ellipse
          cx="8"
          cy="8"
          rx="8"
          ry="8"
          fill="var(--ds-brand-primary-dark)"
        />
        <path
          d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
          stroke="white"
          stroke-width="0.860438"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <div class="card-content-left">
      <div class="main-content-container">
        <div class="text-content-wrapper">
          <div class="title-row">
            <div class="title-with-heart rm-title-heart">
              <span
                class="content-title cursor-pointer"
                :class="{ 'content-title--inquiry': isInquiry(item), 'text-preferred': restaurantStatus === 'Preferred' }"
                @click="showRestaurantDetails(item.market, item.confirmedRestaurant)"
              >
                <span v-if="item.confirmedRestaurant">
                  <b>{{ item.confirmedRestaurant }}</b>
                </span>
                <span v-else-if="isInquiry(item) && item.requestedRestaurantsArray">
                  <b>
                    <template
                      v-for="(restaurantName, index) in item.requestedRestaurantsArray"
                      :key="restaurantName"
                    >
                      <span @click.stop="showRestaurantDetails(item.market, restaurantName)">{{ restaurantName }}{{ index + 1 == item.requestedRestaurantsArray.length ? '' : ', ' }}</span>
                    </template>
                  </b>
                </span>
                <span v-else-if="item.requestedRestaurantsArray">
                  <b>
                    <template v-for="(restaurantName, index) in item.requestedRestaurantsArray">
                      <span
                        :class="item.declines?.[index] ? 'declined' : ''"
                        @click.stop="showRestaurantDetails(item.market, restaurantName)"
                      >
                        <span v-if="item.requestedRestaurantsArray.length == 1">
                          {{ restaurantName }}
                        </span>
                        <span v-else-if="index < 1">
                          {{ restaurantName }}{{ index + 1 == item.requestedRestaurantsArray.length ? '' : ', ' }}
                        </span>
                        <span v-else-if="index < 2">
                          {{ restaurantName }}{{ index + 1 == item.requestedRestaurantsArray.length ? '' : ',&nbsp;...' }}
                        </span>
                      </span>
                    </template>
                  </b>
                </span>
              </span>
              <button
                v-if="primaryRestaurant"
                class="heart-icon-btn heart-unit"
                @click.stop
              >
                <RestaurantHeart
                  ref="bookingHeartRef"
                  :restaurant-name="primaryRestaurantName"
                  :restaurant-market="item.market"
                  :restaurant="primaryRestaurant"
                />
              </button>
            </div>
          </div>
          <div class="sub-content-container">
            <!--
              Group line always reserves its height so rows with and
              without a group stay the same height 
            -->
            <p class="content-subtitle text-truncate">
              {{ hideGroupLine ? '\u00A0' : (primaryRestaurant?.group || '\u00A0') }}
            </p>
            <!--
              Preferred \u2014 plain tappable word under the group (no icon),
              matching the shared card; opens the For Restaurants modal
            -->
            <button
              v-if="restaurantStatus === 'Preferred' && !isInquiry(item)"
              class="preferred-word"
              aria-label="About Preferred restaurants"
              @click.stop="usersStore.isShowingRestaurantsInfoDialog = true"
            >
              Preferred
            </button>
            <p
              v-if="!experienced && !item.directBooking"
              class="content-subtitle status-label"
            >
              {{
                item.reservationType === 'instant' && item.status === 'Accepted' ? 'Booked' : (inquiryLabel(item) || translateStatus[item.status])
              }}
              <!--
                RM-150: when the current user submitted on behalf of a member,
                attribute the status to the member so an admin watching their
                queue sees "Awaiting Response for Galen Mason" at a glance. 
              -->
              <template
                v-if="item?.owner?.fullName && item?.owner?.email && myUser?.email && (item.owner.email.toLowerCase() !== myUser.email.toLowerCase())"
              >
                for {{ item.owner.fullName }}
              </template>
            </p>
            <!-- RM-359: say plainly that RM reviews it before the restaurant sees it -->
            <p
              v-if="item.status === 'Pending RM Review'"
              class="approval-note"
            >
              {{ item.statusNote || reviewLine(requestedNames(item)) }}
            </p>
            <!-- A request RM returned: it is a draft again, with why -->
            <div
              v-else-if="returnedNote"
              class="approval-feedback"
            >
              <span class="approval-feedback-label">{{ returnedNote.label }}</span>
              <span
                v-if="returnedNote.text"
                class="approval-feedback-text"
              >{{ returnedNote.text }}</span>
            </div>
            <p
              v-if="(myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard' || myUser?.primaryGroupPermission == 'owner') && item?.owner?.fullName && (item?.owner?.email != myUser?.email)"
              class="content-subtitle"
            >
              Host:
              {{ item.owner.fullName }}
            </p>
            <p
              v-else-if="item?.submittedBy?.firstName && (item?.submittedBy?.email != item.owner?.email)"
              class="content-subtitle"
            >
              <template v-if="item.status == 'Accepted'">
                Booked by {{ item.submittedBy.firstName }} {{ item.submittedBy.lastName }}
              </template>
              <template v-else>
                Submitted by {{ item.submittedBy.firstName }} {{ item.submittedBy.lastName }}
              </template>
            </p>
            <p
              v-else-if="item?.createdBy?.firstName && (item?.createdBy?.email != item.owner.email)"
              class="content-subtitle"
            >
              Created by {{ item.createdBy.firstName }}
              {{ item.createdBy.lastName }}
            </p>
            <p
              v-if="item.date && item.partySize && (item.confirmedTime || item.preferredTime)"
              class="content-subtitle booking-datetime"
              :class="{ 'text-strikethrough': isCancelled }"
            >
              {{ moment(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a').format("ddd M/D/YY") }} |
              <VIcon
                icon="mdi-account-multiple-outline"
                size="12"
              /> {{ item.partySize }} | {{ moment(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a').format("h:mma") }}
            </p>

            <VExpandTransition>
              <div
                v-if="!experienced && isMemberPreferred"
                class="member-preferred-banner"
              >
                <VIcon
                  icon="mdi-star-check"
                  size="14"
                  class="mr-1"
                />
                <template v-if="memberPreferredGroupName">
                  <span>{{ $t('restaurant.preferred.bannerTitleViaGroup', { groupName: memberPreferredGroupName }) }}</span>
                </template>
                <template v-else>
                  <CmsText
                    k="restaurant.preferred.bannerTitle"
                    tag="span"
                  />
                </template>
              </div>
              <p
                v-else-if="!experienced"
                class="content-subtitle"
                :class="{ 'text-strikethrough': isCancelled }"
              >
                <template v-if="isInquiry(item)">
                  <span
                    v-if="item.minSpendPerPerson && item.partySize"
                    class="inquiry-budget"
                  >
                    Budget: ${{ numberWithCommas(item.minSpendPerPerson) }}pp · ${{ numberWithCommas(item.minSpendPerPerson * item.partySize) }} F&B<template v-if="item.budgetFlexibility && /^no/i.test(item.budgetFlexibility)">, can go higher</template>
                  </span>
                </template>
                <template v-else>
                  <span v-if="item.minSpendPerPerson && item.partySize">
                    F&B: ${{
                      numberWithCommas(item.minSpendPerPerson * item.partySize)
                    }} |
                  </span>
                  <span v-if="formattedSpend(item) != '$'">
                    Total Spend: {{ formattedSpend(item) }}
                  </span>
                </template>
              </p>
            </VExpandTransition>
            <p
              v-if="item.directBooking"
              class="content-subtitle"
            >
              Uploaded {{ moment(item.createdAt).format("M/D/YY [at] h:mma") }}
            </p>
            <p
              v-if="!experienced && item.reservationType == 'general' && item.status == 'Accepted' && !item.directBooking"
              class="content-subtitle"
            >
              Secured {{ newBookingTimingText(item) }}
            </p>
            <p
              v-else-if="item.reservationType == 'private'"
              class="content-subtitle inquiry-budget"
            >
              {{ item.confirmedRestaurant && inquiryOtherRestaurants(item).length
                ? $t('reservation.private.alsoSentTo', { restaurants: inquiryOtherRestaurants(item).join(', ') })
                : $t('reservation.private.inquiryType') }}
            </p>
            <p
              v-if="isInquiry(item) && item.status === 'Submitted'"
              class="content-subtitle inquiry-guidance"
            >
              {{ $t('reservation.private.guidance') }}
            </p>
            <p
              v-if="isInquiry(item) && item.status === 'Approve?'"
              class="content-subtitle"
            >
              ✓ {{ $t('reservation.private.contractReceived') }}
            </p>
            <p
              v-if="item.occasion && !item.directBooking && item.reservationType != 'private'"
              class="content-subtitle"
              :class="{ 'text-strikethrough': isCancelled }"
            >
              {{ item.occasion }}
            </p>

            <!-- Favorite Note -->
            <div
              v-if="myUser && favorited && primaryRestaurant"
              class="favorite-note-text"
              @click.stop="openNoteDialog"
            >
              <VIcon
                size="14"
                :icon="hasNote ? 'mdi-note-text' : 'mdi-note-text-outline'"
                :color="hasNote ? 'primary' : 'grey'"
                class="cursor-pointer"
                style="vertical-align: middle; margin-right: 2px;"
              />
              <span v-if="hasNote">{{ existingNote.note }}</span>
            </div>

            <!-- Friends who favorited -->
            <FriendsLikedChips
              v-if="primaryRestaurantId"
              :restaurant-id="primaryRestaurantId"
              @click="openFansDrawer"
            />

            <div class="actions-row">
              <div class="flex gap-4 mt-1 mb-1 mb-md-1">
                <VBtn
                  v-if="!item.directBooking"
                  variant="tonal"
                  size="small"
                  density="compact"
                  icon
                  color="primary"
                  @click.stop="openBillDetailsDrawer"
                >
                  <VIcon icon="mdi-receipt-text-outline" />
                </VBtn>

                <template v-if="item.status == 'Draft'">
                  <VBtn
                    variant="tonal"
                    size="small"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="safeReservationOperation('editReservation', item._id)"
                  >
                    Review & Submit
                  </VBtn>
                  <VBtn
                    variant="tonal"
                    size="small"
                    color="secondary"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="reservationIdSelected = item._id; isConfirmDeleteDialogOpen = true;"
                  >
                    Delete
                  </VBtn>
                </template>

                <template
                  v-else-if="item.status == 'Not Available' || item.status == 'No Response' || item.status == 'Declined'"
                  class="reservation-item-actions-holder"
                >
                  <VBtn
                    variant="tonal"
                    size="small"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="safeReservationOperation('duplicateReservationAndEdit', item._id)"
                  >
                    Edit &
                    Resubmit
                  </VBtn>
                </template>
                <template
                  v-else-if="isInquiry(item) && item.status === 'Submitted'"
                  class="reservation-item-actions-holder"
                >
                  <!-- RM-400: the one control on a sent inquiry; it cannot be unsent, so no Cancel -->
                  <VBtn
                    variant="outlined"
                    size="small"
                    rounded="pill"
                    class="inquiry-booked-btn"
                    @click.stop="isBookedSheetOpen = true"
                  >
                    {{ $t('reservation.private.bookedQuestion') }}
                  </VBtn>
                </template>
                <template v-else-if="isInquiry(item) && item.status === 'Approve?'" />
                <template
                  v-else-if="item.status == 'Submitted' || item.status == 'Awaiting Response from Restaurant' || item.status == 'Pending RM Review'"
                  class="reservation-item-actions-holder"
                >
                  <VBtn
                    variant="tonal"
                    size="small"
                    color="secondary"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="reservationIdSelected = item._id; isConfirmRescindDialogOpen = true;"
                  >
                    Cancel
                  </VBtn>
                </template>
                <template
                  v-else-if="item.status == 'Accepted' && moment().isBefore(moment.tz(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a', item.timeZone.identifier).clone().subtract(24, 'hours'))"
                  class="reservation-item-actions-holder"
                >
                  <VBtn
                    variant="tonal"
                    size="small"
                    color="secondary"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="safeReservationOperation('cancelReservation', item)"
                  >
                    Cancel
                  </VBtn>

                  <RmToolTip v-if="item.confirmedMessageToMember">
                    <template #text>
                      <VIcon
                        style="margin-left: -3px;"
                        density="compact"
                        color="#5fabd2"
                        size="16"
                      >
                        mdi-message-text-outline
                      </VIcon>
                    </template>
                    <template #description>
                      <p><b>Message from {{ item.confirmedRestaurant }}:</b><br>{{ item.confirmedMessageToMember }}</p>
                    </template>
                  </RmToolTip>
                  <RmToolTip>
                    <template #text>
                      <VIcon
                        style="margin-left: -3px;"
                        density="compact"
                        color="#5fabd2"
                        size="16"
                      >
                        mdi-info-outline
                      </VIcon>
                    </template>
                    <template #description>
                      <p>You'll receive an email/text confirmation from {{ item.confirmedRestaurant }} too</p>
                    </template>
                  </RmToolTip>
                </template>
                <template
                  v-else-if="item.status == 'Member Action'"
                  class="reservation-item-actions-holder"
                >
                  <VBtn
                    variant="tonal"
                    size="small"
                    color="secondary"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="reservationIdSelected = item._id; isConfirmRescindDialogOpen = true;"
                  >
                    Cancel
                  </VBtn>
                </template>
                <template
                  v-else-if="!item.directBooking"
                  class="reservation-item-actions-holder"
                >
                  <VBtn
                    v-if="isWithin24Hours"
                    variant="tonal"
                    size="small"
                    color="secondary"
                    density="compact"
                    style="padding-left: 5px !important; padding-right: 5px !important;"
                    @click="openLateCancelDialog"
                  >
                    Request to Cancel
                  </VBtn>
                  <RmToolTip v-if="item.confirmedMessageToMember">
                    <template #text>
                      <VIcon
                        style="margin-left: -3px;"
                        density="compact"
                        color="#5fabd2"
                        size="16"
                      >
                        mdi-message-text-outline
                      </VIcon>
                    </template>
                    <template #description>
                      <p><b>Message from {{ item.confirmedRestaurant }}:</b><br>{{ item.confirmedMessageToMember }}</p>
                    </template>
                  </RmToolTip>
                </template>
                <template v-if="item.status == 'Accepted'">
                  <RmToolTip v-if="item.stripeHoldConfirmedAmount && !item.stripeHoldReleasedAt && !item.stripeHoldCapturedAmount && moment().isSameOrAfter(moment.tz(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a', item.timeZone.identifier))">
                    <template #text>
                      <VIcon
                        style="margin-left: -3px;"
                        density="compact"
                        color="#5fabd2"
                        size="16"
                      >
                        mdi-info-outline
                      </VIcon>
                    </template>
                    <template #description>
                      <p>
                        Reservation in review -- if you spent the minimum your hold will be released in full by noon
                        the day after your reservation.
                      </p>
                    </template>
                  </RmToolTip>
                </template>
                <template v-if="item.status == 'Not Available' && item.declines && (item.declines.length == item.requestedRestaurantsArray.length)">
                  <div
                    v-for="(decline, index) in item.declines"
                  >
                    <RmToolTip v-if="decline && decline.declineMessageToMember">
                      <template #text>
                        <VIcon
                          style="margin-left: -3px;"
                          density="compact"
                          color="#e1aa3c"
                          size="16"
                        >
                          mdi-message-text-outline
                        </VIcon>
                      </template>
                      <template #description>
                        <p><b>Message from {{ item.requestedRestaurantsArray[index] }}:</b><br>{{ decline.declineMessageToMember }}</p>
                      </template>
                    </RmToolTip>
                  </div>
                </template>

                <!-- Message Restaurant button (Platinum only, Preferred restaurants) -->
                <VBtn
                  v-if="canMessageRestaurant"
                  variant="tonal"
                  size="small"
                  color="primary"
                  density="compact"
                  style="padding-left: 5px !important; padding-right: 5px !important;"
                  prepend-icon="mdi-message-outline"
                  @click.stop="openMessageRestaurant"
                >
                  Message
                </VBtn>
              </div>
            </div>
          </div><!-- /sub-content-container -->
        </div><!-- /text-content-wrapper -->
      </div><!-- /main-content-container -->
    </div><!-- /card-content-left -->
    <div
      class="image-container-right"
    >
      <div class="position-relative restaurant-image-right">
        <RestaurantImage
          v-if="item.market && (item.confirmedRestaurant || item.requestedRestaurantsArray.length > 0)"
          class="h-100 restaurant-image cursor-pointer"
          :restaurant-name="(item.confirmedRestaurant ? [item.confirmedRestaurant] : item.requestedRestaurantsArray)"
          :restaurant-market="item.market"
        />
      </div>
    </div>

    <!-- Note Editor Dialog -->
    <VDialog
      v-model="isNoteDialogOpen"
      max-width="420"
      @click:outside="isNoteDialogOpen = false"
    >
      <VCard class="note-dialog-card">
        <div class="note-dialog-header">
          <VIcon
            icon="mdi-note-text-outline"
            size="20"
            color="primary"
          />
          <span class="note-dialog-title">Store notes for {{ primaryRestaurantName }}</span>
        </div>
        <VCardText class="note-dialog-body">
          <VTextarea
            v-model="noteText"
            placeholder="Favorite dishes, tables, team members, occasions, who recommended..."
            :counter="NOTE_MAX_LENGTH"
            :maxlength="NOTE_MAX_LENGTH"
            rows="3"
            auto-grow
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="note-textarea"
          />
          <p
            v-if="noteError"
            class="text-error text-caption mt-1 mb-0"
          >
            {{ noteError }}
          </p>
        </VCardText>
        <VCardActions class="note-dialog-actions">
          <VSpacer />
          <VBtn
            variant="text"
            size="small"
            @click="isNoteDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :loading="savingNote"
            @click="saveNote"
          >
            Save Note
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.text-strikethrough {
  text-decoration: line-through;
  opacity: 0.7;
}

/* Line-separated row matching the shared restaurant card: thumbnail LEFT
   (order -1), text block, outlined "+" RIGHT (order 10) — no box */
.booking-container {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 2px;
  border: none;
  border-bottom: 1px solid var(--ds-border-subtle);
  max-width: 100%;
  border-radius: 0;
  background: transparent; /* rows sit directly on the page background */
  box-shadow: none;
  box-sizing: border-box;
  overflow: hidden;
  text-align: left;
  line-height: 1.2em;
  position: relative;
}

/* Quiet outlined "+": the button draws the circle; the svg keeps only its
   plus stroke */
.add-btn-left {
  order: 10; /* the only control on the right */
  flex-shrink: 0;
  background: var(--ds-surface-card);
  border: 1.5px solid var(--ds-border-strong);
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  min-width: 30px;
  height: 30px;
  margin-top: 1px;
  color: var(--ds-brand-primary-dark);
  transition: transform 0.2s ease, border-color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  svg ellipse {
    fill: transparent;
  }

  svg path {
    stroke: var(--ds-brand-primary-dark);
    stroke-width: 1.4;
  }

  &:hover {
    border-color: var(--ds-brand-primary-dark);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

.card-content-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

.main-content-container {
  display: flex;
  align-items: flex-start;
  gap: 0px;
}

// Preferred — plain tappable word in brand green, matching the shared card
.preferred-word {
  background: transparent;
  border: none;
  padding: 0;
  width: fit-content;
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--ds-brand-primary-dark);
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
}

.text-content-wrapper {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.title-row {
  margin-bottom: 4px;
  flex-shrink: 0;
}

/* Geometry comes from the global .rm-title-heart spec */

/* Restaurant name — the serif (Playfair 500), brand ink, like every card */
.content-title {
  font-family: var(--ds-font-heading);
  font-size: 20px;
  font-weight: var(--ds-font-medium);
  line-height: 1.25;
  color: var(--ds-brand-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  b {
    font-weight: inherit; /* the serif carries the emphasis */
  }
}

/* Names stay brand ink — the seal signals Preferred */
.text-preferred {
  color: var(--ds-brand-ink);
}

.heart-icon-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Favorites are the lead signal — heart + count in brand green */
  color: var(--ds-brand-primary-dark);
  transition: color 0.2s ease;

  :deep(.favorite-count) {
    color: var(--ds-brand-primary-dark);
  }

  &:hover {
    color: var(--ds-brand-primary);
  }
}

.sub-content-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.content-subtitle {
  font-family: var(--ds-font-body);
  font-size: 13.5px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-body); /* readable ink, not washed-out gray */
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-title--inquiry {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  font-size: 18px;
  line-height: 1.3;
}
.inquiry-budget {
  white-space: normal;
}
.inquiry-guidance {
  color: var(--ds-text-muted);
  font-size: 12.5px;
  white-space: normal;
}
.inquiry-booked-btn {
  border-color: var(--ds-brand-ink);
  color: var(--ds-brand-ink);
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
}
.status-label {
  font-weight: 500;
  color: #666;
}

/* RM-359: the waiting request's one-line explanation, and RM's feedback on a returned draft */
.approval-note {
  margin: 2px 0 0;
  color: #666;
  font-size: 12px;
  line-height: 1.3;
}

.approval-feedback {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 0;
  padding: 6px 8px;
  border-left: 3px solid var(--ds-brand-primary-dark);
  border-radius: 6px;
  background: var(--ds-brand-primary-light);
  font-size: 12px;
  line-height: 1.35;
}

.approval-feedback-label {
  color: var(--ds-brand-primary-dark);
  font-weight: 600;
}

.approval-feedback-text {
  color: #222;
  white-space: pre-line;
}

.booking-datetime {
  display: flex;
  align-items: center;
  gap: 4px;
}

.actions-row {
  padding-top: 2px;
}

.declined {
  position: relative;
}

.declined::after {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  right: 0;
  border-top: 1px solid red;
  transform: translateY(-50%);
}

.declines {
  color: red;
}

.image-container-right {
  order: -1; /* thumbnail leads the row, matching the shared card */
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--ds-radius-md);
  overflow: hidden;
  align-self: flex-start;
  background: var(--ds-brand-primary-light); /* soft tile when no image */

  :deep(.v-img) {
    width: 100% !important;
    height: 100% !important;
  }
}

.restaurant-image-right {
  width: 100%;
  height: 100%;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.member-preferred-banner {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--ds-brand-primary-light);
  border-left: 3px solid var(--ds-brand-primary-dark);
  border-radius: 6px;
  padding: 4px 8px;
  margin: 2px 0;
  color: var(--ds-brand-primary-dark);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
}

.favorite-note-text {
  font-family: var(--ds-font-body);
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: normal;
  word-break: break-word;
  cursor: pointer;
  line-height: 1.3;
  padding-top: 5px;
}

.text-grey {
  color: #666 !important;
}

.bill-details-sheet {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding: 8px 16px 18px;
  min-height: 60vh;
  max-height: 92vh;
  overflow-y: auto;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 20%);
}

.sheet-handle {
  width: 48px;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0);
  margin: 4px auto 10px;
}

.bill-sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.bill-sheet-kicker {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5a6b7d;
  font-weight: 600;
}

.bill-sheet-title {
  font-size: 1rem;
  font-weight: 700;
  color: #132235;
  line-height: 1.3;
}

.bill-sheet-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  color: #5a6b7d;
  font-size: 12px;
}

.bill-sheet-member-line {
  margin-top: 4px;
  color: #5a6b7d;
  font-size: 12px;
  line-height: 1.4;
}

.bill-sheet-member-email {
  color: #8a9aab;
  margin-left: 2px;
}

.bill-sheet-timing {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #2a3f56;
  font-size: 12px;
  line-height: 1.4;
}

.bill-sheet-note {
  margin-top: 6px;
  color: #2a3f56;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-line;
}

.bill-active-details {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bill-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  line-height: 1.4;
}

.bill-detail-label {
  color: #5a6a7c;
  font-weight: 600;
}

.bill-detail-value {
  color: #16283d;
  font-weight: 400;
}

.bill-spend-card {
  background: #ffffff;
  border: 1px solid rgba(22, 40, 61, 0.12);
  border-radius: 12px;
  padding: 12px;
  margin-top: 12px;
}

.bill-spend-card-highlight {
  background: linear-gradient(130deg, #eef5ff 0%, #ffffff 90%);
}

.bill-spend-card-combined {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  background: linear-gradient(130deg, #f6f9ff 0%, #ffffff 80%);
}

.bill-spend-column {
  min-width: 0;
}

.bill-spend-column-total .bill-spend-value {
  color: #114377;
}

.bill-spend-divider {
  width: 1px;
  align-self: stretch;
  background: rgba(22, 40, 61, 0.12);
}

.bill-spend-label {
  font-size: 11px;
  color: #4d637a;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.bill-spend-value {
  font-size: 15px;
  font-weight: 700;
  color: #122940;
}

.bill-preferred-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--ds-brand-primary-light);
  border-left: 3px solid var(--ds-brand-primary-dark);
  border-radius: 12px;
  padding: 12px 14px;
  margin-top: 12px;
  color: var(--ds-brand-primary-dark);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.bill-receipt-cta {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px dashed rgba(26, 91, 189, 0.4);
  background: rgba(26, 91, 189, 0.06);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bill-receipt-copy {
  font-size: 13px;
  color: #1b3550;
  font-weight: 600;
}

.bill-receipt-subcopy {
  font-size: 12px;
  color: #4d637a;
}

.bill-error {
  margin-top: 10px;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}

.bill-receipts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
}

.bill-receipts-title {
  font-size: 13px;
  font-weight: 700;
  color: #1b3550;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bill-empty-state {
  margin-top: 10px;
  padding: 18px 12px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.03);
  color: rgba(var(--v-theme-on-surface), 0.65);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.receipt-image-item {
  margin-top: 10px;
  border: 1px solid rgba(22, 40, 61, 0.1);
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  box-shadow: 0 10px 16px -12px rgba(20, 33, 48, 0.45);
}

.receipt-image-preview {
  width: 100%;
  max-height: 190px;
  object-fit: contain;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.03);
}

.receipt-pdf-preview {
  width: 100%;
  min-height: 120px;
  border-radius: 10px;
  background: linear-gradient(140deg, #fff5f5 0%, #ffffff 90%);
  border: 1px dashed rgba(207, 47, 47, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
}

.receipt-pdf-preview-text {
  font-size: 12px;
  font-weight: 600;
  color: #8b2b2b;
}

@media (max-width: 420px) {
  .bill-spend-card-combined {
    grid-template-columns: 1fr auto 1fr;
    gap: 8px;
  }
}

.note-dialog-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 8px;
}

.note-dialog-title {
  font-size: 14px;
  font-weight: 600;
}

.note-dialog-body {
  padding: 8px 16px 0;
}

.note-dialog-actions {
  padding: 8px 16px 12px;
}

// Mobile responsiveness
// Desktop responsiveness — slightly larger name, same row structure
@media (min-width: 960px) {
  .content-title {
    font-size: 23px;
  }
}

/* Desktop — prototype scale, matching the shared card */
@media (min-width: 960px) {
  .booking-container {
    gap: 16px;
    padding-block: 18px;
  }

  .image-container-right {
    width: 56px;
    height: 56px;
  }
}
</style>
